<template>
  <v-form ref="LIEU" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="LIEU">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <lieu-label v-if="isDisplayed('LABEL')"
                    ref="LABEL"
                    :theme="theme"
                    :showMissing="showMissing"
                    v-model:value=label
                    @status=statusLabel
                    :over-ride-placeholder=placeholderLabel
                    :required="isRequired('LABEL')"
                    :contextId=lieu.id
                    :readOnly="readOnly('LABEL')"
                    :objType=objType
                    :fiche=fiche
                    :sectionName="sectionName">
        </lieu-label>
        <adresse-summary v-if="isDisplayed('ADRESSE_ID')"
                         ref="ADRESSE_ID"
                         :theme="theme"
                         :showMissing="showMissing"
                         v-model:value=adresseId
                         :moduleId=moduleId
                         libelle="'Adresse'"
                         newLabel=''
                         selectText=""
                         @status=statusAdresseId
                         :autoClear=true
                         :owner="owner"
                         :requiredAttributs="requiredAttributs"
                         :autoSelect=true
                         :ressource=ressourceAdresseSummary
                         :filters=adresseIdFilter
                         :toggleReset=resetAdresseId
                         :required="isRequired('ADRESSE_ID')"
                         :contextId=lieu.id
                         :readOnly="readOnly('ADRESSE_ID')"
                         :objType=objType
                         :fiche=fiche
                         :sectionName="sectionName">
        </adresse-summary>
        <graph-control ref="GRAPH_CONTROL"
                       :obj="getGraphObj"
                       :objType="getGraphObjType()"
                       :sectionName="sectionName"
                       :toggle="toggleGraph"
                       :disabled="readOnly('GRAPH_CONTROL')"
                       v-model:value=graphAnswers
                       @setMinimum="setMinimum"
                       @status="statusGraph">
        </graph-control>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudLieu from '@/generated/cruds/CrudLieu';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import Owner from '@/extend/classes/Owner';
  import { ColumnFilter, ListChoix } from '@/extend/classes/ListData';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudLieu,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.LIEU,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderLabel: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },

      ressourceAdresseSummary () { return RessourceGenControl(GenControl.LIEU_ADRESSE_SUMMARY); },

      ressource () { return RessourceGenSection(GenSection.LIEU); },

      owner (): Owner {
        const owner = new Owner();
        owner.objType = ObjTypes.LIEU;
        owner.objId = this.editLieu.id;
        return owner;
      },

      fields (): string[] {
        return ['LABEL', 'ADRESSE_ID', 'GRAPH_CONTROL'];
      },

      extraButtons (): Button[] {
        return [];
      },
      getGraphObj (): unknown {
        return this.editLieu;
      },


      adresseIdFilter (): ColumnFilter[] {
        const res: ColumnFilter[] = [];
        return res;
      },
    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (!this.editLieu.canUpdate) {
          return true;
        }
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("LABEL", false);
          this.readOnlyMap.set("ADRESSE_ID", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("LABEL", true);
          this.displayMap.set("ADRESSE_ID", true);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("LABEL", true);
          this.requiredMap.set("ADRESSE_ID", true);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoLieu' 
      },

      getGraphObjType (): ObjTypes {
        return this.objType;
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
